import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { SelectOption, TutorialInstruction, User } from '../../../utils/models';
import { concatClassNames, matchPhrases } from '../../../utils/utilities';
import { SUPER_ADMIN_ROLE_NAME, tutorialConstants } from '../../../utils/constants';
import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { getCurrentUser } from '../../../services/authService';
import { getCanAccessReportCreation } from '../../../utils/session';
import { getSingleTabSession, keyPrefix, removeSingleTabSession, storeSingleTabSession, updateSingleTabSession } from '../../../services/singleTabService';
import { updateReportLastPhotoNumber } from '../../../services/reportService';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCDRReport from '../../custom-reports/custom-cdr/createCustomCDRReport';
import CreateCustomPCNLReport from '../../custom-reports/custom-pcnl/createCustomPCNLReport';
import CustomLPTReport from '../../custom-reports/custom-lpt/createCustomLPTReport';
import CustomMPIReport from '../../custom-reports/custom-mpi/createCustomMPIReport';
import CustomETReport from '../../custom-reports/custom-et/createCustomETReport';
import CustomDRTReport from '../../custom-reports/custom-drt/createCustomDRTReport';
import CustomFVIPipingReport from '../../custom-reports/custom-visual-inspection/createCustomFVIPipingReport';
import CreateHelideckTieDownReport from './helideck-tie-down/createHelideckTieDownReport';
import CreateHelideckReport from './helideck/createHelideckReport';
import CreateHelideckNetDropReport from './helideck-net-drop/createHelideckNetDropReport';
import CreateCustomFVITankReport from '../../custom-reports/custom-visual-inspection/createCustomFVITankReport';
import CreateCustomExxonMobilRiserReport from '../../custom-reports/custom-riser/createCustomExxonMobilRiser';
import CreatePAUTReport from './paut/createPAUTReport';
import CreateETReport from './et/createETReport';
import CreateMTReport from './mt/createMTReport';
import CreateMUTReport from './mut/createMUTReport';
import CreateDPTReport from './dpt/createDPTReport';
import CreatePDOReport from '../../custom-reports/custom-pdo/createPDOReport';
import CreateRTWReport from './rt-weld/createRTWReport';
import CreateCustomPDRTReport from '../../custom-reports/custom-pdrt/createCustomPDRTReport';
import CreateVisualInspectionReport from './visual-inspection/createVisualInspectionReport';
import CreateVTPipingReport from './vt/createVTPipingReport';
import CreateVTEquipmentReport from './vt-equipment/createVTEquipmentReport';
import CreateVTStructureReport from './vt-structure/createVTStructureReport';
import CreateCustomExxonMobileAutReport from '../../custom-reports/custom-aut/createCustomExxonMobilAutReport';
import CreateCustomExxonMobilePECReport from '../../custom-reports/custom-pec/createCustomExxonMobilPECReport';
import CreateCustomNDTUTMStructureReport from '../../custom-reports/custom-visual-inspection/createCustomNDTStructuralReport';
import CustomFVIEquipmentReport from '../../custom-reports/custom-visual-inspection/createCustomFVIEquipmentReport';
import CustomFVIStructureReport from '../../custom-reports/custom-visual-inspection/createCustomFVIStructureReport';
import CreateCustomExxonMobilUTMPipingReport from '../../custom-reports/custom-utm/createCustomExxonMobilUTMPipingReport';
import CreateCustomExxonMobilUTMEquipmentReport from '../../custom-reports/custom-utm/createCustomExxonMobilUTMEquipmentReport';
import CreateCustomExxonMobilUTMStructureReport from '../../custom-reports/custom-utm/createCustomExxonMobilUTMStructureReport';
import CreateCustomHardnessReport from '../../custom-reports/custom-hardness/createCustomHardness';
import CreateCustomPMIReport from '../../custom-reports/custom-pmi/createCustomPMI';
import CreateCustomTOFDReport from '../../custom-reports/custom-tofd/createCustomTOFDReport';
import CreateCustomPAUTReport from '../../custom-reports/custom-paut/createCustomPAUTReport';
import CreateCustomUTMTankReport from '../../custom-reports/custom-utm/createCustomUTMTankReport';
import CreateCustomInfraredReport from '../../custom-reports/custom-infrared/createCustomInfrared';
import CreateLRUTReport from '../../custom-reports/custom-lrut/createLRUTReport';
import CreateSWUTReport from '../../custom-reports/custom-swut/createSWUTReport';
import CustomFVIGREReport from '../../custom-reports/custom-visual-inspection/createCustomFVIGREReport';
import Elevation from '../../themed/instruction-modals/elevation';
import FAQs from '../../blocks/faqs';
import Header from '../../blocks/header';
import Input from '../../themed/input/input';
import Instruction from '../../themed/instruction-modals/instruction';
import Modal from '../../themed/modal';
import Loader from '../../themed/loader';
import Logs from '../logs';
import ProjectCoverDataUpdate from '../../forms/projectCoverDataUpdate';
import ReportCacheNotice from '../../themed/report-cache';
import ReportProgressTrail from '../../themed/report-progress-trail';
import Select from '../../themed/select/select';

import RiskRatingContext from '../../../context/useRiskRatingContext';

import useProjectDetails from '../../../hooks/useProjectDetails';
import useActiveProjectBlock from '../../../hooks/useNotify';

import withInspectionTypes from '../../hoc/withInspectionTypes';
import withProjects from '../../hoc/withProjects';

import classNames from './pdf.module.scss';
import config from '../../../utils/config';
import routes from '../../../utils/routes';

interface NewReportProps {
	typesLoading: boolean;
	types: SelectOption[];
}

const NewReport: FC<NewReportProps> = (props) => {
	const [riskRating, setRiskRating] = React.useState(null);
	const [isLogsVisible, setLogsVisible] = React.useState(false);
	const [isCoverDataEditVisible, setCoverDataEditVisible] = React.useState(false);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { roleName, role } = getCurrentUser() as User;
	const { projectId } = useParams();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { itemCountPerPage, lastPhotoNumber } = useAppSelector(getReportDetails);

	const project = useProjectDetails('always');

	const handleNavigatToProjectDetails = useCallback(() => {
		navigate('/dashboard/projects/' + projectId);
	}, [navigate, projectId]);

	const handleActiveTutorial = useCallback((tutorialId: number) => {
		dispatch(setActiveTutorial(tutorialId));

		if (tutorialId === tutorialConstants.VIEW_UNPUBLISHED_REPORT) {
			const path: string = `/dashboard/projects/${projectId}/progress`;
			navigate(path);
		}
	}, [navigate, projectId, dispatch]);

	const handleItemCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const newItemCount: number = e.currentTarget.valueAsNumber;
		dispatch(setItemsPerPage(newItemCount));
	}, [dispatch]);

	const handleToggleLogsVisible = useCallback(() => {
		setLogsVisible((previous) => !previous);
	}, []);

	const handleToggleCoverDataEditVisible = useCallback(() => {
		setCoverDataEditVisible((previous) => !previous);
	}, []);

	const ReportComponent = useMemo(() => {
		const ReportMapping = {
			[config.supportedTemplates.hardnessInspectionReport]: <CreateCustomHardnessReport />,
			[config.supportedTemplates.infrared]: <CreateCustomInfraredReport />,
			[config.supportedTemplates.pmi]: <CreateCustomPMIReport />,
			[config.supportedTemplates.cdr]: <CustomCDRReport />,
			[config.supportedTemplates.gre]: <CustomFVIGREReport />,
			[config.supportedTemplates.pdo]: <CreatePDOReport />,
			[config.supportedTemplates.lrut]: <CreateLRUTReport />,
			[config.supportedTemplates.tankUTM]: <CreateCustomUTMTankReport />,
			[config.supportedTemplates.tofd]: <CreateCustomTOFDReport />,
			[config.supportedTemplates.pautNew]: <CreateCustomPAUTReport />,
			[config.supportedTemplates.exxonMobilRiser]: <CreateCustomExxonMobilRiserReport />,
			[config.supportedTemplates.exxonMobilFVITank]: <CreateCustomFVITankReport />,
			[config.supportedTemplates.pcnlStructuralIntegrityReport]: <CreateCustomPCNLReport />,
			[config.supportedTemplates.exxonMobilUTMStructure]: <CreateCustomExxonMobilUTMStructureReport />,
			[config.supportedTemplates.exxonMobilUTMEquipment]: <CreateCustomExxonMobilUTMEquipmentReport />,
			[config.supportedTemplates.exxonMobilUTMPiping]: <CreateCustomExxonMobilUTMPipingReport />,
			[config.supportedTemplates.exxonMobilPEC]: <CreateCustomExxonMobilePECReport />,
			[config.supportedTemplates.exxonMobilAUT]: <CreateCustomExxonMobileAutReport />,
			[config.supportedTemplates.exxonMobilDRT]: <CustomDRTReport />,
			[config.supportedTemplates.exxonMobilET]: <CustomETReport />,
			[config.supportedTemplates.exxonMobilMPI]: <CustomMPIReport />,
			[config.supportedTemplates.exxonMobilLPT]: <CustomLPTReport />,
			[config.supportedTemplates.exxonMobilFVIStructure]: <CustomFVIStructureReport />,
			[config.supportedTemplates.exxonMobilFVIPiping]: <CustomFVIPipingReport />,
			[config.supportedTemplates.exxonMobilFVIEquipment]: <CustomFVIEquipmentReport />,
			[config.supportedTemplates.helideck]: <CreateHelideckReport />,
			[config.supportedTemplates.helideckNetDrop]: <CreateHelideckNetDropReport />,
			[config.supportedTemplates.helideckTieDown]: <CreateHelideckTieDownReport />,
			[config.supportedTemplates.et]: <CreateETReport />,
			[config.supportedTemplates.paut]: <CreatePAUTReport />,
			[config.supportedTemplates.dpt]: <CreateDPTReport />,
			[config.supportedTemplates.pdrt]: <CreateCustomPDRTReport />,
			[config.supportedTemplates.rtw]: <CreateRTWReport />,
			[config.supportedTemplates.swut]: <CreateSWUTReport />,
			[config.supportedTemplates.mt]: <CreateMTReport />,
			[config.supportedTemplates.mut]: <CreateMUTReport />,
			[config.supportedTemplates.vtPiping]: <CreateVTPipingReport />,
			[config.supportedTemplates.vtEquipment]: <CreateVTEquipmentReport />,
			[config.supportedTemplates.vtStructure]: <CreateVTStructureReport />,
			[config.supportedTemplates.visualInspection]: <CreateVisualInspectionReport />,
			[config.supportedTemplates.ndtStructuralReport]: <CreateCustomNDTUTMStructureReport />
		};

		const inspectionTypeId = project.projectDetails?.inspectionTypeId;
		return inspectionTypeId ? ReportMapping[inspectionTypeId] : null;
	}, [project.projectDetails?.inspectionTypeId]);

	const handleStorageEvent = useCallback((event: StorageEvent) => {
		if (event.key?.startsWith(keyPrefix) && event.oldValue && !event.newValue) {
			updateSingleTabSession(event.key, event.oldValue);
		}
	}, []);

	const handleClearSession = useCallback(() => {
		if (project.projectDetails) removeSingleTabSession(project.projectDetails._id);
	}, [project.projectDetails]);

	const projectTitle = useMemo((): string => {
		return 'Create a Report For ' + (project.projectDetails?.projectTitle || 'N/A');
	}, [project.projectDetails?.projectTitle]);

	const reportClassName = useMemo((): string => {
		return concatClassNames([classNames['new-report-body-report'], 'row']);
	}, []);

	useActiveProjectBlock();

	useEffect(() => {
		if (project.projectDetails && !getCanAccessReportCreation(project.projectDetails)) {
			navigate(routes.unauthorized.fullPath);
		}
	}, [project.projectDetails, navigate]);

	useEffect(() => {
		window.addEventListener('storage', handleStorageEvent);
		window.addEventListener('beforeunload', handleClearSession);

		return () => {
			window.removeEventListener('storage', handleStorageEvent);
			window.removeEventListener('beforeunload', handleClearSession);
		}
	}, [handleStorageEvent, handleClearSession]);

	useEffect(() => {
		if (project.projectDetails?._id) {
			if (getSingleTabSession(project.projectDetails._id)) {
				alert('You already have this report open on a tab!') ;
				navigate(`/dashboard/projects/${project.projectDetails._id}`, { replace: true });
			} else {
				storeSingleTabSession(project.projectDetails._id)
			}
		}

		return () => {
			handleClearSession();
		}
	}, [navigate, handleClearSession, project.projectDetails?._id]);

	useEffect(() => {
		if (project.projectDetails && project.projectDetails.lastPhoneNumber > 0) {
			dispatch(setLastPhotoNumber(project.projectDetails.lastPhoneNumber));
		}

		// eslint-disable-next-line
	}, [dispatch, project.projectDetails?.lastPhoneNumber]);

	useEffect(() => {
		if (lastPhotoNumber && project.projectDetails && lastPhotoNumber !== project.projectDetails.lastPhoneNumber) {
			updateReportLastPhotoNumber({
				projectId: project.projectDetails._id, 
				lastReportPhotoNumber: lastPhotoNumber,
			});
		}

		// eslint-disable-next-line
	}, [lastPhotoNumber, project.projectDetails?.lastPhoneNumber]);

	const isReportType = activeTutorial === tutorialConstants.REPORT_TYPE;
	const isAutoSave = activeTutorial === tutorialConstants.AUTOSAVE_REPORT;
	const isSections = activeTutorial === tutorialConstants.SECTION_COMPLETED;

	return (
		<React.Fragment>
			{instructions.map((instruction) => (
				<Instruction
					key={instruction.id}
					isVisible={activeTutorial === instruction.id}
					onProceed={() => handleActiveTutorial(instruction.next)}
					title={instruction.title}
					message={instruction.message}
				/>
			))}

			<Modal isVisible={isLogsVisible} onClose={handleToggleLogsVisible} style={{ width: '90%', height: '90%' }}>
				<Logs projectId={projectId as string} />
			</Modal>

			<Modal isVisible={isCoverDataEditVisible} onClose={handleToggleCoverDataEditVisible} style={{ height: 'auto' }}>
				<ProjectCoverDataUpdate />
			</Modal>

			<Header
				canViewCTA
				onCTAClick={handleNavigatToProjectDetails}
				subtitle={projectTitle}
				buttonIcon="kanban-fill"
				buttonTitle="Back to Project Details"
			/>

			<RiskRatingContext.Provider value={{ riskRating, setRiskRating }}>
				<div className={classNames['new-report-body']}>
					<Loader isVisible={project.isFetching || props.typesLoading} />

					<Conditional isVisible={!!project.projectDetails}>
						<React.Fragment>
							<div className="container-fluid overflow-hidden">
								<Elevation isElevated={isReportType}>
									<div className={reportClassName}>
										<div className="col">
											<Input
												type="number"
												label="Items Per Page"
												name="itemCount"
												value={itemCountPerPage}
												onChange={handleItemCountChange}
											/>
										</div>

										<div className="col">
											<Select
												label="Report Type"
												value={project.projectDetails?.inspectionTypeId}
												disabled
												options={props.types}
												name="report-type"
												placeholder="Select Report Type"
											/>
										</div>
									</div>
								</Elevation>
							</div>

							<Conditional isVisible={role.approveReports}>
								<div className="d-flex justify-content-end">
									<div className={concatClassNames([classNames['new-report-body-table'], 'd-flex p-2'])}>
										<Conditional isVisible={config.enableViewLogs && matchPhrases(roleName, SUPER_ADMIN_ROLE_NAME)}>
											<div className="me-2">
												<Button 
													icon='bookshelf'
													label='View Logs' 
													onClick={handleToggleLogsVisible} 
													style={{ marginTop: 0 }}
												/>
											</div>
										</Conditional>

										<Button 
											icon='code-square' 
											label='Update Scope' 
											onClick={handleToggleCoverDataEditVisible} 
											style={{ marginTop: 0 }}
										/>
									</div>
								</div>
							</Conditional>

							<Elevation isElevated={isAutoSave}>
								<ReportCacheNotice reportId={projectId as string} />
							</Elevation>

							<Elevation isElevated={isSections}>
								<div className={classNames['new-report-body-table']}>
									<div className="p-3 bg-light border">
										<div className="p-3 bg-white border">
											{ReportComponent}
										</div>
									</div>
								</div>
							</Elevation>

							<ReportProgressTrail projectId={projectId} />
						</React.Fragment>
					</Conditional>

					<Conditional isVisible={project.error}>
						<div className="d-flex justify-content-center">
							<Button
								label="Try again"
								disabled={project.isFetching}
								onClick={project.fetchProjectDetails}
							/>
						</div>
					</Conditional>

					<FAQs />
				</div>
			</RiskRatingContext.Provider>
		</React.Fragment>
	);
};

const instructions: TutorialInstruction[] = [
	{
		id: tutorialConstants.NEW_REPORT,
		next: tutorialConstants.REPORT_TYPE,
		title: 'Create Reports Here!',
		message: 'This is where the magic happens! Create, preview and submit Project reports from this page.',
	},
	{
		id: tutorialConstants.REPORT_TYPE,
		next: tutorialConstants.AUTOSAVE_REPORT,
		title: 'Inspection Type!',
		message: 'The inspection type is already prefilled. Peak here to know the inspection type. This is not editable',
	},
	{
		id: tutorialConstants.AUTOSAVE_REPORT,
		next: tutorialConstants.SECTION_COMPLETED,
		title: 'Autosave Progress!',
		message: 'Your progress is automatically saved when you make updates. You will get a notice asking you to confirm progress',
	},
	{
		id: tutorialConstants.SECTION_COMPLETED,
		next: tutorialConstants.PREVIEW_REPORT,
		title: 'Fill Sections!',
		message: 'Each section represents a part of the report. Once a section is completed, a green tick will be visible indicating that the section is complete!',
	},
	{
		id: tutorialConstants.PREVIEW_REPORT,
		next: tutorialConstants.VIEW_UNPUBLISHED_REPORT,
		title: 'Preview Report!',
		message: "See what the results look like! Simply click the 'Save and Preview Report' button to view",
	},
];

const Report = withProjects(withInspectionTypes(NewReport));
export default React.memo(Report);
